import type { CoasterProject } from '../schema';
import type {
  DiscoveryCatalogPage,
  OwnerProjectState,
  PublicCatalogPage,
  PublicCoasterCard,
  PublicCoasterPayload,
  PublicCoasterRecord,
  PublicCoasterViewerState,
  PublicCoasterViewerStateBatch,
} from './contracts';

export const EMPTY_VIEWER_STATE: PublicCoasterViewerState = Object.freeze({
  boosted: false,
  favorited: false,
  isOwner: false,
});

export function mergeDiscoveryCatalog(
  page: PublicCatalogPage,
  viewerStates?: PublicCoasterViewerStateBatch | null,
): DiscoveryCatalogPage {
  return {
    ...page,
    items: page.items.map<PublicCoasterCard>((card) => ({
      ...card,
      viewerState: viewerStates?.items[card.publicCoasterId] ?? EMPTY_VIEWER_STATE,
    })),
  };
}

function publicRuntimeId(kind: 'project' | 'revision', value: string): string {
  return `public.${kind}.${value.replace(/[^A-Za-z0-9._-]/g, '').slice(0, 96) || 'unknown'}`;
}

export function hydratePublicCoaster(payload: PublicCoasterPayload): PublicCoasterRecord {
  const source = payload.lineage?.sourcePublicCoasterId;
  const root = payload.lineage?.rootPublicCoasterId ?? source;
  const project = structuredClone(payload.definition) as CoasterProject;
  project.projectId = publicRuntimeId('project', payload.publicCoasterId);
  project.revisionId = publicRuntimeId('revision', payload.contentHash);
  project.metadata = {
    ...payload.definition.metadata,
    creator: {
      kind: 'buncha',
      id: payload.creator.publicCreatorId,
      displayName: payload.creator.displayName,
    },
  };
  project.permissions = {
    visibility: 'public',
    allowReadOnlyShare: false,
    allowRemix: payload.allowRemix,
  };
  project.lineage =
    source && root
      ? {
          sourceProjectId: publicRuntimeId('project', source),
          sourceRevisionId: publicRuntimeId('revision', source),
          rootProjectId: publicRuntimeId('project', root),
          remixedAt: payload.publishedAt,
        }
      : null;
  return {
    publicCoasterId: payload.publicCoasterId,
    publicSlug: payload.publicSlug,
    title: payload.title,
    description: payload.description,
    creator: payload.creator,
    themeId: payload.themeId,
    heroUrl: payload.heroUrl,
    allowRemix: payload.allowRemix,
    publishedAt: payload.publishedAt,
    contentHash: payload.contentHash,
    lineage: payload.lineage,
    intensity: payload.intensity,
    stats: payload.stats,
    project,
  };
}

export function ownerProjectStatusLabel(
  state: OwnerProjectState,
): 'draft_private' | 'pending_review' | 'public' | 'rejected' | 'hidden' | 'archived' {
  if (state.lifecycle === 'archived') return 'archived';
  if (state.visibility === 'hidden') return 'hidden';
  if (state.workingRevisionStatus === 'pending') return 'pending_review';
  if (state.visibility === 'public') return 'public';
  if (state.workingRevisionStatus === 'rejected') return 'rejected';
  return 'draft_private';
}
